import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** A catalogue row: the product and its category name (`null` = uncategorized). */
export interface OfferCatalogueItem {
	productId: string
	code: string
	name: string
	categoryName: string | null
}

/** A product in the offer's ordered selection. */
export interface OfferSelectedItem {
	productId: string
	name: string
}

/** The view's built-in copy; override any entry for localisation. */
export interface OfferProductSelectionLabels {
	catalogueTitle: string
	selectionTitle: string
	uncategorized: string
	empty: string
	noneSelected: string
	add: string
	added: string
	moveUp: string
	moveDown: string
	remove: string
	save: string
	saving: string
}

const DEFAULT_LABELS: OfferProductSelectionLabels = {
	catalogueTitle: 'Products',
	selectionTitle: 'Selected for this offer',
	uncategorized: 'Uncategorized',
	empty: 'No active products',
	noneSelected: 'No products selected',
	add: 'Add',
	added: 'Added',
	moveUp: 'Move up',
	moveDown: 'Move down',
	remove: 'Remove',
	save: 'Save',
	saving: 'Saving…',
}

/**
 * Props for {@link OfferProductSelectionView}.
 *
 * @property catalogue - The products to choose from, already in display order.
 * @property selected - The offer's ordered selection (position = index).
 * @property readOnly - True when the offer can no longer be edited; hides every control.
 * @property canSave - Enables Save.
 * @property saving - True while a save is in flight.
 * @property message - A notice or refused-save message to show.
 */
export interface OfferProductSelectionViewProps {
	catalogue: readonly OfferCatalogueItem[]
	selected: readonly OfferSelectedItem[]
	readOnly: boolean
	canSave: boolean
	saving: boolean
	message?: string | null
	onAdd: (productId: string) => void
	onMoveUp: (productId: string) => void
	onMoveDown: (productId: string) => void
	onRemove: (productId: string) => void
	onSave: () => void
	labels?: Partial<OfferProductSelectionLabels>
}

type Labels = OfferProductSelectionLabels

/** The catalogue column: each product with Add (disabled once selected). */
function CatalogueColumn(props: {
	catalogue: readonly OfferCatalogueItem[]
	selectedIds: ReadonlySet<string>
	readOnly: boolean
	onAdd: (productId: string) => void
	labels: Labels
}): ReactElement {
	const { catalogue, selectedIds, readOnly, onAdd, labels } = props
	return (
		<section aria-labelledby="offer-catalogue-title" className="flex flex-col gap-3">
			<h2 id="offer-catalogue-title" className="text-ds-lg font-medium text-ds-text">
				{labels.catalogueTitle}
			</h2>
			{catalogue.length === 0 ? (
				<p className="text-ds-sm text-ds-text-muted">{labels.empty}</p>
			) : (
				<ul aria-label={labels.catalogueTitle} className="flex flex-col divide-y divide-ds-border">
					{catalogue.map((item) => (
						<li key={item.productId} className="flex items-center justify-between gap-3 py-2">
							<span className="flex flex-col">
								<span className="text-ds-base text-ds-text">{item.name}</span>
								<span className="text-ds-xs text-ds-text-muted">
									{item.categoryName ?? labels.uncategorized} · {item.code}
								</span>
							</span>
							{readOnly ? null : (
								<Button
									type="button"
									size="sm"
									variant="secondary"
									aria-label={`${labels.add} ${item.name}`}
									isDisabled={selectedIds.has(item.productId)}
									onPress={() => onAdd(item.productId)}
								>
									{selectedIds.has(item.productId) ? labels.added : labels.add}
								</Button>
							)}
						</li>
					))}
				</ul>
			)}
		</section>
	)
}

/** One selected product's Move up / Move down / Remove controls. */
function RowActions(props: {
	item: OfferSelectedItem
	first: boolean
	last: boolean
	onMoveUp: (productId: string) => void
	onMoveDown: (productId: string) => void
	onRemove: (productId: string) => void
	labels: Labels
}): ReactElement {
	const { item, first, last, onMoveUp, onMoveDown, onRemove, labels } = props
	const action = (label: string, onPress: () => void, isDisabled = false) => (
		<Button
			type="button"
			size="sm"
			variant="muted"
			aria-label={`${label} ${item.name}`}
			isDisabled={isDisabled}
			onPress={onPress}
		>
			{label}
		</Button>
	)
	return (
		<span className="flex gap-2">
			{action(labels.moveUp, () => onMoveUp(item.productId), first)}
			{action(labels.moveDown, () => onMoveDown(item.productId), last)}
			{action(labels.remove, () => onRemove(item.productId))}
		</span>
	)
}

/** The refused-save / notice message and the Save button (hidden when read-only). */
function SaveFooter(props: {
	readOnly: boolean
	canSave: boolean
	saving: boolean
	message?: string | null
	onSave: () => void
	labels: Labels
}): ReactElement {
	const { readOnly, canSave, saving, message, onSave, labels } = props
	return (
		<>
			{message ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{message}
				</p>
			) : null}
			{readOnly ? null : (
				<div>
					<Button type="button" onPress={onSave} isDisabled={!canSave || saving}>
						{saving ? labels.saving : labels.save}
					</Button>
				</div>
			)}
		</>
	)
}

/**
 * Offer Wizard Step 2 (TO-123): the catalogue in the order given, and the
 * offer's ordered selection with move up / move down / remove and Save. Pure:
 * the rows, the state flags and the callbacks are props; it neither orders nor
 * fetches anything.
 */
export function OfferProductSelectionView(props: OfferProductSelectionViewProps): ReactElement {
	const { catalogue, selected, readOnly, onAdd } = props
	const labels = { ...DEFAULT_LABELS, ...props.labels }

	return (
		<div className="grid gap-6 md:grid-cols-2">
			<CatalogueColumn
				catalogue={catalogue}
				selectedIds={new Set(selected.map((s) => s.productId))}
				readOnly={readOnly}
				onAdd={onAdd}
				labels={labels}
			/>
			<section aria-labelledby="offer-selection-title" className="flex flex-col gap-3">
				<h2 id="offer-selection-title" className="text-ds-lg font-medium text-ds-text">
					{labels.selectionTitle}
				</h2>
				{selected.length === 0 ? (
					<p className="text-ds-sm text-ds-text-muted">{labels.noneSelected}</p>
				) : (
					<ol aria-label={labels.selectionTitle} className="flex flex-col divide-y divide-ds-border">
						{selected.map((item, index) => (
							<li key={item.productId} className="flex items-center justify-between gap-3 py-2">
								<span className="text-ds-base text-ds-text">
									{index + 1}. {item.name}
								</span>
								{readOnly ? null : (
									<RowActions
										{...props}
										item={item}
										first={index === 0}
										last={index === selected.length - 1}
										labels={labels}
									/>
								)}
							</li>
						))}
					</ol>
				)}
				<SaveFooter {...props} labels={labels} />
			</section>
		</div>
	)
}
